/**
 * The standing verification protocol for an authored sidecar. Ruling 33,
 * 18 September 2026.
 *
 * Three checks, run and reported every time — by Captain Holloway before
 * handover, and here on load. A sidecar that has not been through them is not
 * accepted.
 *
 * **Failures are reported, never silently corrected.** Nothing here reorders a
 * mark to fix an ordering complaint, and nothing adjusts a grid to make a
 * distance look better. A check that finds something wrong says so and leaves
 * the file alone.
 *
 * **Only check 1 verifies anything.** Checks 2 and 3 are consistency and
 * plausibility: they can show that a sidecar contradicts itself or describes
 * something a squadron could not hold, and they cannot show that a grid is
 * right. {@link CHECK_DESCRIPTIONS} carries that wording so it reaches any
 * output rather than living in a comment.
 */

import {
  type SpacingJudgement,
  type SpacingScope,
  judgeSpacing,
} from "./spacing-figures";

/** A six-figure grid is 3 digits of easting and 3 of northing, 100 m each. */
const HUNDRED_METRES = 100;

/** Ruling 33 check 1. A disagreement beyond this fails the sidecar. */
export const ANCHOR_TOLERANCE_M = 500;

export const CHECK_DESCRIPTIONS = Object.freeze({
  anchor:
    "ANCHOR. Verification. Every grid stated verbatim in the order set, against the estimate for the same referent.",
  ordering:
    "ORDERING. Consistency, not verification. Whether the grids reproduce the spatial order the trace shows.",
  plausibility:
    "PLAUSIBILITY. Plausibility, not verification. Distances as measured, for judgement.",
});

/**
 * Metres between two six-figure grids.
 *
 * Both are assumed to be in the same 100 km square, which is what a single
 * trace sheet gives. Across a square boundary the digits wrap and this would
 * read a short distance as a long one; the caller is comparing marks from one
 * sheet, and a cross-square pair would show up as an absurd distance rather
 * than a quiet error.
 */
export function gridSeparationMetres(left: string, right: string): number {
  const east = Number(left.slice(0, 3)) - Number(right.slice(0, 3));
  const north = Number(left.slice(3, 6)) - Number(right.slice(3, 6));
  return Math.hypot(east, north) * HUNDRED_METRES;
}

export interface AnchorPair {
  /** The marker the estimate came from. */
  readonly id: string;
  /** The sidecar's estimate for this referent. */
  readonly estimate: string;
  readonly referent: string;
  /** The grid stated verbatim in the order set. */
  readonly stated: string;
}

export interface StatedGrid {
  readonly grid: string;
  /** The name the order set gives it, as written. */
  readonly label: string;
}

export interface LabelledMark {
  readonly grid: string;
  /** Only a `low-confidence` mark is anchored. Ruling 34. */
  readonly gridState: string;
  readonly id: string;
  readonly label: string;
  readonly referent: string;
}

function labelKey(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Only the estimates are anchored. Ruling 34, 18 September 2026.
 *
 * Where the completed order set names a mark, the sidecar takes that value
 * verbatim and marks it confirmed. Anchoring a taken value against the order
 * set would compare it with itself and report a clean 0 m that means nothing
 * — the check would grade the copy rather than the estimate.
 */
export function estimatesOnly(
  marks: readonly LabelledMark[],
): readonly LabelledMark[] {
  return marks.filter((mark) => mark.gridState === "low-confidence");
}

/**
 * Pairs a mark with the grid the order set states for the **same thing**.
 *
 * By label, not by referent. A trace carries five start points, one per
 * troop, and the order set states a grid for each; pairing them by referent
 * would compare troop one's start point against troop four's and report four
 * disagreements that are only a pairing error. A first attempt did exactly
 * that, and reported an anchor failure that was mine and not the sidecar's.
 *
 * A mark whose label the order set does not carry produces no pair. It is not
 * anchored, and an unanchored mark is reported as unanchored rather than
 * quietly passed.
 */
export function pairAnchorsByLabel(
  marks: readonly LabelledMark[],
  stated: readonly StatedGrid[],
): readonly AnchorPair[] {
  return partitionEstimates(marks, stated).anchored;
}

export interface RestingMark {
  readonly id: string;
  readonly label: string;
  readonly referent: string;
}

export interface EstimatePartition {
  /** Estimates the order set names, and so can be anchored. */
  readonly anchored: readonly AnchorPair[];
  /** Estimates the order set does not name, and never will. */
  readonly resting: readonly RestingMark[];
}

/**
 * Splits the estimates into those that can be anchored and those that
 * **rest**. Ruling 41, 19 September 2026.
 *
 * A mark the order set does not name has no stated value to take, now or
 * later, so its estimate is not an outstanding item of work — it is the
 * finished state of that mark. Enemy positions are the standing case: an
 * order set describes the enemy, it does not grid him.
 *
 * The distinction matters for what a report says. Before Ruling 41 these
 * marks made the anchor check report "nothing to run on", which reads as a
 * gap. They are not a gap. They are excluded, counted, and named.
 */
export function partitionEstimates(
  marks: readonly LabelledMark[],
  stated: readonly StatedGrid[],
): EstimatePartition {
  const anchored: AnchorPair[] = [];
  const resting: RestingMark[] = [];
  for (const mark of estimatesOnly(marks)) {
    const key = labelKey(mark.label);
    const match = stated.find((entry) => labelKey(entry.label) === key);
    if (match === undefined) {
      resting.push({ id: mark.id, label: mark.label, referent: mark.referent });
      continue;
    }
    anchored.push({
      id: mark.id,
      referent: mark.referent,
      stated: match.grid,
      estimate: mark.grid,
    });
  }
  return { anchored, resting };
}

export interface AnchorDisagreement {
  readonly id: string;
  readonly referent: string;
  readonly separationM: number;
}

export interface AnchorResult {
  readonly checked: number;
  readonly disagreements: readonly AnchorDisagreement[];
  /**
   * `all-resting` where every estimate is one the order set will never name,
   * which is a finished state and not a gap. `not-run` where there was no
   * estimate at all. Ruling 41.
   */
  readonly outcome: "all-resting" | "failed" | "not-run" | "passed";
  /** Estimates excluded because nothing will ever state a value for them. */
  readonly resting: readonly RestingMark[];
  readonly worstSeparationM: number | null;
}

/**
 * Check 1. The only one of the three that verifies anything, because it
 * compares an estimate against a value somebody wrote down on purpose.
 */
export function checkAnchor(
  pairs: readonly AnchorPair[],
  resting: readonly RestingMark[] = [],
): AnchorResult {
  if (pairs.length === 0) {
    return {
      checked: 0,
      disagreements: [],
      outcome: resting.length === 0 ? "not-run" : "all-resting",
      resting,
      worstSeparationM: null,
    };
  }
  const separations = pairs.map((pair) => ({
    id: pair.id,
    referent: pair.referent,
    separationM: gridSeparationMetres(pair.stated, pair.estimate),
  }));
  const disagreements = separations.filter(
    ({ separationM }) => separationM > ANCHOR_TOLERANCE_M,
  );
  const worst = separations.reduce(
    (highest, { separationM }) => Math.max(highest, separationM),
    0,
  );
  return {
    checked: pairs.length,
    disagreements,
    outcome: disagreements.length === 0 ? "passed" : "failed",
    resting,
    worstSeparationM: worst,
  };
}

/**
 * Check 2. Consistency, not verification. **Pairwise**, by Ruling 39 of
 * 19 September 2026.
 *
 * ACRIB para 0370: each start point has a corresponding release point. A
 * release point is compared with **its own numbered start point and nothing
 * else** — the release point should sit east of the start point it belongs
 * to.
 *
 * The earlier version compared across the whole set, and reported three
 * faults that were not there: it measured troop one's release point against
 * troop four's start point. A set comparison needs the set to be one thing
 * moving, and four troops are not.
 *
 * A mark with no partner is **UNPAIRED** and is compared against nothing. It
 * is reported, because an unpaired start point is worth knowing about, and it
 * is not a fault.
 *
 * Nothing is reordered, and no other pairing is checked: ACRIB states this
 * one, and inventing a second would be hard rule 4.
 */

export interface OrderedMark {
  readonly grid: string;
  readonly id: string;
  /** The name the trace gives it, which carries the number. */
  readonly label?: string;
  readonly referent: string;
}

export interface OutOfSequenceMark {
  readonly easting: number;
  readonly id: string;
  /** The start point it should sit east of, and does not. */
  readonly notEastOf: string;
  readonly referent: string;
}

export interface UnpairedMark {
  readonly id: string;
  readonly referent: string;
  /** Which partner is missing, in one word. */
  readonly missing: "release-point" | "start-point";
}

export interface OrderingResult {
  /** Pairs that could be compared at all. */
  readonly comparable: number;
  readonly outOfSequence: readonly OutOfSequenceMark[];
  readonly unpaired: readonly UnpairedMark[];
}

/** The number in a label such as "SP 3", or null where there is none. */
function markNumber(mark: OrderedMark): string | null {
  const match = /(\d+)\s*$/.exec(mark.label ?? "");
  return match?.[1] ?? null;
}

export function checkOrdering(marks: readonly OrderedMark[]): OrderingResult {
  const numbered = (referent: string): Map<string, OrderedMark> => {
    const found = new Map<string, OrderedMark>();
    for (const mark of marks) {
      if (mark.referent !== referent) continue;
      const number = markNumber(mark);
      if (number !== null) found.set(number, mark);
    }
    return found;
  };
  const starts = numbered("start-point");
  const releases = numbered("release-point");

  const outOfSequence: OutOfSequenceMark[] = [];
  const unpaired: UnpairedMark[] = [];
  let comparable = 0;

  for (const [number, release] of releases) {
    const start = starts.get(number);
    if (start === undefined) {
      unpaired.push({
        id: release.id,
        referent: release.referent,
        missing: "start-point",
      });
      continue;
    }
    comparable += 1;
    const east = Number(release.grid.slice(0, 3));
    if (east < Number(start.grid.slice(0, 3))) {
      outOfSequence.push({
        easting: east,
        id: release.id,
        notEastOf: start.id,
        referent: release.referent,
      });
    }
  }
  for (const [number, start] of starts) {
    if (!releases.has(number)) {
      unpaired.push({
        id: start.id,
        referent: start.referent,
        missing: "release-point",
      });
    }
  }
  // A start point or release point whose label carries no number cannot be
  // matched to a partner at all. It is unpaired in the plainest sense, and
  // reporting it is the point: a start point nobody can pair is worth
  // knowing about, and guessing which release point it belongs to from its
  // position would be exactly the inference Ruling 39 removed.
  for (const mark of marks) {
    if (markNumber(mark) !== null) continue;
    if (mark.referent === "start-point") {
      unpaired.push({
        id: mark.id,
        referent: mark.referent,
        missing: "release-point",
      });
    }
    if (mark.referent === "release-point") {
      unpaired.push({
        id: mark.id,
        referent: mark.referent,
        missing: "start-point",
      });
    }
  }
  return { comparable, outOfSequence, unpaired };
}

export interface Spacing {
  readonly fromId: string;
  readonly metres: number;
  readonly toId: string;
}

export interface PlausibilityResult {
  /** East-west extent of the marks, in metres. */
  readonly extentEastingM: number;
  /** North-south extent of the marks, in metres. */
  readonly extentNorthingM: number;
  /** Pairs a locked figure judged, and found outside it. */
  readonly flagged: readonly SpacingJudgement[];
  /** Distance between each pair of marks, nearest first, each judged. */
  readonly judged: readonly (Spacing & {
    readonly judgement: SpacingJudgement;
  })[];
  /** Pairs no locked figure covers. Reported, never flagged. */
  readonly noLockedFigure: number;
  /** Phase-to-phase distances, where the sidecar carries phases. */
  readonly phaseDistancesM: readonly number[];
}

/**
 * Says which locked figure, if any, covers a pair of marks.
 *
 * Nothing here decides a scope from the distance, which would be choosing the
 * figure that makes the number look best. It decides from what the two marks
 * **are**, and for two control measures on a trace the answer is that no
 * source describes them — Ruling 37 names squadron frontage in the advance,
 * troop frontage in a guard, spacing between areas of interest and LD-to-LOE
 * depth as expressly not locked.
 *
 * It is a function rather than a constant `null` because the day a sidecar
 * carries vehicle positions, this is the one place that has to change.
 */
export function scopeForPair(): SpacingScope | null {
  return null;
}

/**
 * Check 3. Plausibility, not verification.
 *
 * Every pair is measured and judged. A pair a locked figure covers is judged
 * against it, in scope, with its citation. A pair no figure covers reports
 * `NO_LOCKED_FIGURE` and the distance, and is not flagged.
 */
export function checkPlausibility(
  marks: readonly OrderedMark[],
  phasePathLengthsM: readonly number[] = [],
): PlausibilityResult {
  const eastings = marks.map((mark) => Number(mark.grid.slice(0, 3)));
  const northings = marks.map((mark) => Number(mark.grid.slice(3, 6)));
  const spread = (values: readonly number[]): number =>
    values.length === 0
      ? 0
      : (Math.max(...values) - Math.min(...values)) * HUNDRED_METRES;

  const judged: (Spacing & { judgement: SpacingJudgement })[] = [];
  for (const [index, mark] of marks.entries()) {
    for (const other of marks.slice(index + 1)) {
      const metres = gridSeparationMetres(mark.grid, other.grid);
      judged.push({
        fromId: mark.id,
        toId: other.id,
        metres,
        judgement: judgeSpacing(metres, scopeForPair()),
      });
    }
  }
  judged.sort((left, right) => left.metres - right.metres);

  return {
    extentEastingM: spread(eastings),
    extentNorthingM: spread(northings),
    flagged: judged
      .map(({ judgement }) => judgement)
      .filter(({ verdict }) => verdict === "above" || verdict === "below"),
    judged,
    noLockedFigure: judged.filter(
      ({ judgement }) => judgement.verdict === "no-locked-figure",
    ).length,
    phaseDistancesM: phasePathLengthsM,
  };
}

export interface SidecarVerification {
  /**
   * Whether the sidecar may be used as Source A. Ruling 36: **a sidecar that
   * fails check 1 is refused.**
   *
   * A check that could not run does not refuse. That is a change of position
   * and Ruling 34 is the reason for it: once every mark the order set names
   * carries the stated value verbatim, there is nothing left for the anchor
   * check to compare, and `not-run` becomes the ordinary state of a properly
   * completed sidecar rather than a gap in it. Refusing on `not-run` would
   * refuse exactly the files Ruling 34 asks for.
   *
   * {@link SidecarVerification.verified} keeps the distinction that
   * "permitted" and "checked against something" are not the same thing.
   */
  readonly accepted: boolean;
  readonly anchor: AnchorResult;
  readonly ordering: OrderingResult;
  readonly plausibility: PlausibilityResult;
  /** True only where check 1 ran and passed. Permitted is not verified. */
  readonly verified: boolean;
}

/**
 * All three checks. Only check 1 can refuse a sidecar: Ruling 33 fails on an
 * anchor disagreement over 500 m and reports the other two.
 */
export function verifySidecar(
  anchors: readonly AnchorPair[],
  marks: readonly OrderedMark[],
  phasePathLengthsM: readonly number[] = [],
  resting: readonly RestingMark[] = [],
): SidecarVerification {
  const anchor = checkAnchor(anchors, resting);
  return {
    accepted: anchor.outcome !== "failed",
    anchor,
    ordering: checkOrdering(marks),
    plausibility: checkPlausibility(marks, phasePathLengthsM),
    verified: anchor.outcome === "passed",
  };
}
